// Text entry — the room code, a player name, the feedback body.
//
// **Not MUI's `TextField`.** That is a label, a helper line, a legend, an
// outline notch and four variants; what this interface asks of an input is a
// plate with an edge that lights brand on focus. `invalid` is the one state
// worth a colour, because a refused room code has to say so.

import { styled, type CSSObject, type Theme } from "@mui/material/styles";

import { motion } from "./motion";

export interface FieldOwnProps {
  invalid?: boolean;
}

function skin(theme: Theme, invalid?: boolean): CSSObject {
  const c = theme.kit.color;
  return {
    width: "100%",
    boxSizing: "border-box",
    background: `color-mix(in srgb, ${c.panelRaised} 85%, transparent)`,
    border: `2px solid ${invalid ? c.rust : c.line}`,
    borderRadius: theme.kit.radius.md,
    color: c.ink,
    fontFamily: theme.kit.font.body,
    fontSize: 14,
    lineHeight: 1.4,
    padding: "8px 12px",
    ...motion([], ["border-color", "background"]),
    "&::placeholder": { color: c.inkDim, opacity: 1 },
    "&:focus": { outline: "none", borderColor: invalid ? c.rust : c.brand },
    "&:disabled": { opacity: 0.38, pointerEvents: "none" },
    "@media (pointer: coarse)": { minHeight: theme.kit.touchTarget }
  };
}

const own = (prop: string) => prop !== "invalid";

export const Field = styled("input", { shouldForwardProp: own })<FieldOwnProps>(
  ({ theme, invalid }) => skin(theme, invalid)
);

export const TextArea = styled("textarea", { shouldForwardProp: own })<FieldOwnProps>(
  ({ theme, invalid }) => ({
    ...skin(theme, invalid),
    resize: "vertical",
    minHeight: 96
  })
);
